// VideoSection.js — Bloque de video responsive (Vimeo, YouTube o archivo MP4)
//
// El admin pega la URL tal como la copia del navegador y acá se traduce al
// embed correcto. El marco mantiene la proporción elegida (16:9 por defecto)
// para que no haga saltar el layout mientras carga.
import React from 'react';
import styled from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '3rem 1.5rem'};
  background: ${p => p.$bg || 'transparent'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '900px'};
  margin: 0 auto;
`;

const Titulo = styled.h2`
  margin: 0 0 1.2rem;
  text-align: ${p => p.$align || 'center'};
  color: ${p => p.$color || '#0f355b'};
  font-size: ${p => p.$size || '2rem'};
  font-weight: ${p => p.$weight || 700};
  font-family: ${p => p.$font || 'inherit'};
`;

const Marco = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${p => p.$ratio || '16 / 9'};
  border-radius: ${p => p.$radius || '14px'};
  ${p => p.$border ? `border: ${p.$border};` : ''}
  overflow: hidden;
  background: #000;
  box-shadow: ${p => p.$shadow || '0 10px 30px rgba(15,23,42,0.18)'};
  iframe, video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const Pie = styled.p`
  margin: 0.8rem 0 0;
  text-align: ${p => p.$align || 'center'};
  color: ${p => p.$color || '#64748b'};
  font-size: ${p => p.$size || '0.9rem'};
`;

/* Botones de Acceder / Registrarse bajo el video. Son los mismos que el menú,
   repetidos aquí porque en móvil el menú se pliega en la hamburguesa y hay que
   dejar la entrada a la vista (con `botones_solo_movil` desaparecen en el
   escritorio, que es como está la home de HepCampus). */
const Botones = styled.div`
  display: flex;
  flex-direction: ${p => p.$columna ? 'column' : 'row'};
  align-items: center;
  justify-content: center;
  gap: ${p => p.$gap || '12px'};
  margin: ${p => p.$margin || '1.5rem 0 0'};
  ${p => p.$soloMovil && '@media (min-width: 981px) { display: none; }'}
`;

const Boton = styled.button`
  background: ${p => p.$bg || 'transparent'};
  color: ${p => p.$color || '#ffffff'};
  border: ${p => p.$borde || '2px solid #ffffff'};
  border-radius: ${p => p.$radio || '24px'};
  padding: ${p => p.$padding || '10px 34px'};
  font-size: ${p => p.$tamano || '17px'};
  font-weight: ${p => p.$peso || 600};
  font-family: inherit;
  cursor: pointer;
  min-width: ${p => p.$ancho || '147px'};
  max-width: 100%;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

// Traduce la URL que pega el admin al embed que corresponde.
// Devuelve { tipo: 'iframe' | 'archivo', src }.
export const resolverVideo = (url) => {
  const u = (url || '').trim();
  if (!u) return null;

  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:[/?#]h=|\/)?([0-9a-f]{8,})?/i);
  if (vimeo) {
    const hash = vimeo[2] || (u.match(/[?&]h=([0-9a-f]+)/i) || [])[1];
    return { tipo: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}${hash ? `?h=${hash}` : ''}` };
  }

  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
  if (yt) return { tipo: 'iframe', src: `https://www.youtube-nocookie.com/embed/${yt[1]}` };

  if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(u)) {
    return { tipo: 'archivo', src: u.startsWith('http') ? u : `${BACKEND_BASE}/${u.replace(/^\//, '')}` };
  }

  // Cualquier otra cosa se asume ya lista para incrustar.
  return { tipo: 'iframe', src: u };
};

const VideoSection = ({ config, openLogin, openRegister, tl }) => {
  const c = config || {};
  const video = resolverVideo(c.url);
  if (!video) return null;

  const poster = c.poster
    ? (c.poster.startsWith('http') ? c.poster : `${BACKEND_BASE}/${c.poster.replace(/^\//, '')}`)
    : undefined;
  const titulo = tl ? tl(c.titulo) : c.titulo;
  const pie = tl ? tl(c.pie) : c.pie;
  const align = c.alineacion || 'center';

  return (
    <Wrapper $bg={buildBg(c, 'transparent')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        {titulo && (
          <Titulo
            $align={align}
            $color={c.color_titulo}
            $size={c.tamano_titulo}
            $weight={c.peso_titulo}
            $font={c.fuente_titulo}
          >
            {titulo}
          </Titulo>
        )}
        <Marco $ratio={c.proporcion} $radius={c.radio} $shadow={c.sombra} $border={c.borde}>
          {video.tipo === 'archivo' ? (
            <video
              src={video.src}
              poster={poster}
              controls
              playsInline
              preload="metadata"
              autoPlay={c.autoplay === true}
              muted={c.autoplay === true || c.silenciado === true}
              loop={c.loop === true}
            />
          ) : (
            <iframe
              src={video.src}
              title={titulo || 'Video'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              loading="lazy"
            />
          )}
        </Marco>
        {pie && <Pie $align={align} $color={c.color_pie} $size={c.tamano_pie}>{pie}</Pie>}
        {(c.boton_login || c.boton_registro) && (
          <Botones
            $columna={c.botones_en_columna === true}
            $gap={c.gap_botones}
            $margin={c.margen_botones}
            $soloMovil={c.botones_solo_movil === true}
          >
            {c.boton_login && (
              <Boton
                type="button"
                onClick={() => openLogin?.()}
                $bg={c.color_boton_login}
                $color={c.color_texto_boton_login}
                $borde={c.borde_boton_login}
                $radio={c.radio_botones}
                $padding={c.padding_botones}
                $tamano={c.tamano_botones}
                $peso={c.peso_botones}
                $ancho={c.ancho_minimo_botones}
              >
                {tl ? tl(c.texto_boton_login, 'Acceder') : (c.texto_boton_login || 'Acceder')}
              </Boton>
            )}
            {c.boton_registro && (
              <Boton
                type="button"
                onClick={() => openRegister?.()}
                $bg={c.color_boton_registro}
                $color={c.color_texto_boton_registro}
                $borde={c.borde_boton_registro}
                $radio={c.radio_botones}
                $padding={c.padding_botones}
                $tamano={c.tamano_botones}
                $peso={c.peso_botones}
                $ancho={c.ancho_minimo_botones}
              >
                {tl ? tl(c.texto_boton_registro, 'Registrarse') : (c.texto_boton_registro || 'Registrarse')}
              </Boton>
            )}
          </Botones>
        )}
      </Inner>
    </Wrapper>
  );
};

export default VideoSection;
